import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Truck } from "lucide-react";
import { api, cardProduct, inr, FALLBACK_IMG } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

// Server-driven free-delivery nudge — ERP returns free_delivery_above (null = feature off),
// amount_to_free_delivery and free_delivery_unlocked in every cart response.
export default function FreeDeliveryNudge({ totals, items }) {
  const { addToCart, pincodeInfo } = useStore();
  const [suggestions, setSuggestions] = useState([]);

  const threshold = totals?.free_delivery_above;
  const unlocked = totals?.free_delivery_unlocked === true;
  const remaining = totals?.amount_to_free_delivery ?? 0;
  const showNudge = threshold != null && !unlocked && remaining > 0;

  useEffect(() => {
    if (!showNudge) { setSuggestions([]); return; }
    const inCart = new Set((items || []).map((i) => i.product_id));
    api.get("/ecom/catalog/products", {
      params: {
        max_price: Math.min(Math.max(Math.ceil(remaining) + 100, 149), 399),
        page_size: 12,
        ...(pincodeInfo?.store_id && { store_id: pincodeInfo.store_id }),
      },
    })
      .then((r) => {
        const picks = (r.data?.items || [])
          .filter((p) => !inCart.has(p.id) && (p.in_stock ?? true))
          .map((p) => cardProduct(p))
          .sort((a, b) => a.price - b.price)
          .slice(0, 3);
        setSuggestions(picks);
      })
      .catch(() => setSuggestions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNudge, Math.ceil(remaining / 100), pincodeInfo?.store_id]);

  if (threshold == null) return null;

  if (unlocked) {
    return (
      <p data-testid="free-delivery-unlocked" className="flex items-center gap-2 rounded-2xl border border-gold/40 bg-gold/10 px-4 py-2.5 text-xs font-bold text-gold">
        <Truck className="h-4 w-4 shrink-0" /> You've unlocked FREE delivery
      </p>
    );
  }

  const progress = threshold > 0 ? Math.min(100, ((threshold - remaining) / threshold) * 100) : 0;

  return (
    <div data-testid="free-delivery-nudge" className="rounded-2xl border border-champagne/40 bg-moss p-4">
      <p className="flex items-center gap-2 text-xs font-bold text-cream">
        <Truck className="h-4 w-4 shrink-0 text-gold" />
        Add {inr(remaining)} more for <span className="text-gold">FREE delivery</span>
      </p>
      <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-forest">
        <motion.div
          data-testid="nudge-progress"
          initial={false}
          animate={{ width: `${progress}%` }}
          transition={{ type: "spring", stiffness: 120, damping: 20 }}
          className="h-full rounded-full bg-gold"
        />
      </div>
      {suggestions.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid="nudge-suggestions">
          {suggestions.map((s) => (
            <li key={s.id} className="flex items-center gap-3">
              <img
                src={s.img}
                onError={(e) => { if (!e.currentTarget.dataset.fbk) { e.currentTarget.dataset.fbk = "1"; e.currentTarget.src = FALLBACK_IMG; } }}
                alt={s.name}
                className="h-9 w-9 shrink-0 rounded-lg border border-champagne/40 object-cover"
              />
              <div className="min-w-0 flex-1">
                <p className="line-clamp-1 text-[11px] text-cream">{s.name}</p>
                <p className="font-mono text-[10px] font-bold text-gold">{inr(s.price)}</p>
              </div>
              <button
                data-testid={`nudge-add-${s.id}`}
                onClick={() => addToCart(s.id, 1)}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-cream text-forest transition-colors hover:bg-gold"
                aria-label={`Add ${s.name} to bag`}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
